import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface BatchDetail {
  batchId: number;
  batchCode: string;
  sourceTypeId: number;
  sourceTypeName: string;
  vendorId: number;
  vendorName: string;
  productId: number;
  productName: string;
  manufactureDate: string;
  expiryDate: string;
  stock: number;
  availableStock: number;
  allocatedStock: number;
}

interface BatchRow {
  batch: BatchDetail | null;
  quantity: number | null;
}

interface AcceptOutwardRequestDialogProps {
  visible: boolean;
  onHide: () => void;
  request: any;
  onSuccess: () => void;
}

const AcceptOutwardRequestDialog: React.FC<AcceptOutwardRequestDialogProps> = ({
  visible,
  onHide,
  request,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const submitInProgress = useRef(false);
  const [batches, setBatches] = useState<BatchDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");
  const [rows, setRows] = useState<BatchRow[]>([{ batch: null, quantity: null }]);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (visible && request) {
      fetchBatchDetails();
      setRows([{ batch: null, quantity: null }]);
      setNote("");
      setErrors([]);
    }
  }, [visible, request]);

  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const fetchBatchDetails = async () => {
    if (!request) return;

    try {
      setLoading(true);
      const response = await posService.getBatchDetailsByProductId(
        request.productId,
        request.requestToPOS
      );

      if (response && Array.isArray(response)) {
        setBatches(response);
      } else {
        setBatches([]);
        showMessage("Warning", "No batches available for this product", "warn");
      }
    } catch (error) {
      console.error("Error fetching batch details:", error);
      showMessage("Error", "Failed to load batch details", "error");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    setRows([...rows, { batch: null, quantity: null }]);
    setErrors([]);
  };

  const deleteRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated.length > 0 ? updated : [{ batch: null, quantity: null }]);
    setErrors([]);
  };

  const updateRow = (index: number, key: keyof BatchRow, value: any) => {
    const updated = [...rows];
    updated[index][key] = value;
    setRows(updated);
    setErrors([]);
  };

  const getAvailableBatchOptions = (currentIndex: number) => {
    const selectedBatchIds = rows
      .map((row, idx) => (idx !== currentIndex && row.batch ? row.batch.batchId : null))
      .filter((id) => id !== null);

    return batches.filter((batch) => !selectedBatchIds.includes(batch.batchId));
  };

  const validateRows = (): boolean => {
    const validationErrors: string[] = [];

    // Filter out completely empty rows (both batch and quantity are null)
    const nonEmptyRows = rows.filter(
      (row) => row.batch !== null || row.quantity !== null
    );

    // Check if at least one row is filled
    if (nonEmptyRows.length === 0) {
      validationErrors.push("Please select at least one batch and enter quantity.");
      setErrors(validationErrors);
      return false;
    }

    // Validate each non-empty row
    nonEmptyRows.forEach((row) => {
      const actualIndex = rows.indexOf(row);

      if (row.batch && !row.quantity) {
        validationErrors.push(
          `Row ${actualIndex + 1}: Please enter quantity for selected batch.`
        );
      }
      if (row.quantity && !row.batch) {
        validationErrors.push(
          `Row ${actualIndex + 1}: Please select a batch for entered quantity.`
        );
      }
      if (row.batch && row.quantity) {
        // Check if quantity exceeds available stock
        if (row.quantity > row.batch.availableStock) {
          validationErrors.push(
            `Row ${actualIndex + 1}: Quantity (${row.quantity}) exceeds available stock (${
              row.batch.availableStock
            }) for batch ${row.batch.batchCode}.`
          );
        }
        // Check if quantity is less than or equal to 0
        if (row.quantity <= 0) {
          validationErrors.push(
            `Row ${actualIndex + 1}: Quantity must be greater than 0.`
          );
        }
      }
    });

    // Check for duplicate batch selection (only for rows with selected batch)
    // Using safer approach to avoid undefined errors
    const selectedBatches: number[] = [];
    rows.forEach((row) => {
      if (row.batch && row.batch.batchId !== undefined && row.batch.batchId !== null) {
        selectedBatches.push(row.batch.batchId);
      }
    });

    const duplicates = selectedBatches.filter(
      (id, index) => selectedBatches.indexOf(id) !== index
    );

    if (duplicates.length > 0) {
      validationErrors.push("Same batch cannot be selected multiple times.");
    }

    // Calculate total quantity (only from rows with both batch and quantity)
    const totalQuantity = rows
      .filter((row) => row.batch !== null && row.quantity !== null && row.quantity > 0)
      .reduce((sum, row) => sum + (row.quantity || 0), 0);

    // Check if total quantity exceeds requested quantity
    if (totalQuantity === 0) {
      validationErrors.push("Total quantity cannot be 0.");
    } else if (totalQuantity > request.quantity) {
      validationErrors.push(
        `Total quantity (${totalQuantity}) exceeds requested quantity (${request.quantity}).`
      );
    }

    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const getTotalQuantity = () => {
    return rows
      .filter((row) => row.batch !== null && row.quantity !== null && row.quantity > 0)
      .reduce((sum, row) => sum + (row.quantity || 0), 0);
  };

  const handleSubmit = async () => {
    if (loading || submitInProgress.current) return;
    if (!validateRows()) {
      showMessage("Validation Error", "Please fix the errors before submitting.", "error");
      return;
    }

    submitInProgress.current = true;
    try {
      setSubmitting(true);

      const batchAllocations = rows
        .filter((row) => row.batch && row.quantity)
        .map((row) => ({
          batchId: row.batch!.batchId,
          quantity: row.quantity!,
        }));

      const payload = {
        requestId: request.id,
        productId: request.productId,
        posId: request.requestToPOS, // Sending POS
        toPosId: request.requestFromPOS, // Receiving POS
        sourceTypeId: 1, // Inter-POS transfer
        documentDetails: request.documentDetails,
        note: note,
        batchAllocations: batchAllocations,
      };

      const response = await posService.updateoutwardrequestaccept(payload);

      if (response && response.isSuccess) {
        showMessage("Success","Request accepted successfully", "success");
        onSuccess();
        submitInProgress.current = false;
        handleClose();
      } else {
        showMessage(
          "Error",
          response?.message || "Failed to accept request",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error accepting request:", error);
      showMessage(
        "Error",
        error?.response?.data?.message || "Failed to accept request",
        "error"
      );
    } finally {
      submitInProgress.current = false;
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitInProgress.current) return;
    setRows([{ batch: null, quantity: null }]);
    setNote("");
    setErrors([]);
    onHide();
  };

  const requestedQuantity = request?.quantity ?? 0;
  const availableForMovements = request?.availableForMovements ?? 0;
  const totalQuantity = getTotalQuantity();
  const exceedsRequestedQuantity = totalQuantity > requestedQuantity;
  const insufficientStock = requestedQuantity > availableForMovements;
  const exceedsAvailableForMovements = totalQuantity > availableForMovements;
  const maximumForRow = (index: number) => {
    const batch = rows[index].batch;
    if (!batch) return undefined;
    const otherQuantity = rows.reduce((sum, row, rowIndex) =>
      rowIndex !== index && row.batch && row.quantity && row.quantity > 0 ? sum + row.quantity : sum, 0);
    return Math.max(0, Math.min(batch.availableStock, requestedQuantity - otherQuantity));
  };

  const footer = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleClose}
        className="p-button-text"
        disabled={submitting}
      />
      <Button
        label="Accept & Send"
        icon="pi pi-check"
        onClick={handleSubmit}
        loading={submitting}
        disabled={loading || submitting || exceedsRequestedQuantity || exceedsAvailableForMovements}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={handleClose}
        header="Accept Outward Request"
        modal
        closable={!submitting}
        style={{ width: "60vw" }}
        breakpoints={{ "960px": "80vw", "641px": "95vw" }}
        footer={footer}
      >
        {/* Request Details Section */}
        <div className="mb-4 p-3 surface-100 border-round">
          <h4 className="mt-0 mb-3">Request Details</h4>
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="mb-2">
                <strong>Requested From:</strong> {request?.requestFromPosCode}
              </div>
              <div className="mb-2">
                <strong>Product:</strong> {request?.productName}
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="mb-2">
                <strong>Requested To:</strong> {request?.requestToPosCode}
              </div>
              <div className="mb-2">
                <strong>Requested Quantity:</strong>{" "}
                <span className="font-bold text-primary">{request?.quantity}</span>
              </div>
            </div>
          </div>

          {/* Stock Information Below */}
          <div className="grid mt-3 pt-3" style={{ borderTop: "1px solid #e0e0e0" }}>
            <div className="col-12 md:col-4">
              <div className="p-3" style={{ backgroundColor: "#F5F5F5", borderRadius: "4px" }}>
                <div className="text-sm" style={{ color: "#666" }}>Available Stock</div>
                <div className="font-bold text-lg" style={{ color: "#333", marginTop: "0.5rem" }}>
                  {request?.availableStock ?? 0}
                </div>
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="p-3" style={{ backgroundColor: "#F5F5F5", borderRadius: "4px" }}>
                <div className="text-sm" style={{ color: "#666" }}>Open Order Demand</div>
                <div className="font-bold text-lg" style={{ color: "#333", marginTop: "0.5rem" }}>
                  {request?.openOrderDemand ?? 0}
                </div>
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="p-3" style={{ backgroundColor: "#E3F2FD", borderRadius: "4px" }}>
                <div className="text-sm" style={{ color: "#1565C0" }}>Available for Movements</div>
                <div className="font-bold text-lg" style={{ color: "#1565C0", marginTop: "0.5rem" }}>
                  {request?.availableForMovements ?? 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Batch Selection Section */}
        <div className="mb-3">
          <div className="flex justify-content-between align-items-center mb-3">
            <h4 className="mt-0 mb-0">Select Batches & Quantity</h4>
            <Button
              label="Add Another Batch"
              icon="pi pi-plus"
              className="p-button-sm p-button-success px-4 py-2"
              onClick={addRow}
              disabled={loading || submitting || batches.length === 0}
            />
          </div>

          {loading ? (
            <div className="text-center p-4">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
              <p>Loading batch details...</p>
            </div>
          ) : batches.length > 0 ? (
            <>
              {rows.map((row, index) => (
                <div
                  key={index}
                  className="flex align-items-center gap-2 mb-3"
                  style={{ flexWrap: "wrap" }}
                >
                  <Dropdown
                    inputId={`outward-batch-${index}`}
                    aria-label={`Batch ${index + 1}`}
                    disabled={submitting}
                    value={row.batch}
                    options={getAvailableBatchOptions(index)}
                    onChange={(e) => updateRow(index, "batch", e.value)}
                    optionLabel="batchCode"
                    placeholder="Select Batch"
                    className="flex-1"
                    // style={{ minWidth: "200px" }}
                    style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                    filter
                    showClear
                    itemTemplate={(option) => (
                      <div>
                        <div className="font-bold">{option.batchCode}</div>
                        <div className="text-sm text-500">
                          Available: {option.availableStock}
                        </div>
                      </div>
                    )}
                  />

                  <InputNumber
                    inputId={`outward-quantity-${index}`}
                    aria-label={`Quantity for batch ${index + 1}`}
                    disabled={submitting}
                    value={row.quantity}
                    onValueChange={(e) => updateRow(index, "quantity", e.value)}
                    placeholder="Quantity"
                    min={0}
                    max={maximumForRow(index)}
                    className="flex-1"
                    // style={{ minWidth: "150px" }}
                    style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    useGrouping={false}  
                  />

                  {rows.length > 1 && (
                    <Button
                      icon="pi pi-trash"
                      className="p-button-danger"
                      onClick={() => deleteRow(index)}
                      tooltip="Remove this batch"
                      disabled={submitting}
                    />
                  )}
                </div>
              ))}

              {/* Total Quantity Display */}
              <div className="flex justify-content-between align-items-center p-3 bg-gray-100 border-round mt-3">
                <strong>Total Quantity:</strong>
                <span
                  className={`text-xl font-bold ${
                    totalQuantity > 0 && totalQuantity <= requestedQuantity
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {totalQuantity} / {requestedQuantity}
                </span>
              </div>
            </>
          ) : (
            <div className="text-center p-4">
              <p className="text-gray-500">No batches available for this product</p>
            </div>
          )}
        </div>

        {exceedsRequestedQuantity && <p role="alert" className="text-red-600">
          Total quantity cannot exceed the requested quantity ({requestedQuantity}).
        </p>}

        {exceedsAvailableForMovements && <p role="alert" className="text-red-600 mb-3 p-3 bg-red-100 border-round">
          <i className="pi pi-exclamation-triangle mr-2"></i>
          Total quantity ({totalQuantity}) cannot exceed Available for Movements ({availableForMovements}).
        </p>}

        {/* Validation Errors */}
        {errors.length > 0 && (
          <div className="mb-3 p-3 bg-red-100 border-round">
            <h5 className="mt-0 mb-2 text-red-700">
              <i className="pi pi-exclamation-triangle mr-2"></i>
              Validation Errors:
            </h5>
            <ul className="mt-0 mb-0 pl-4">
              {errors.map((error, index) => (
                <li key={index} className="text-red-700 mb-1">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Note Section */}
        <div className="mb-3">
          <label htmlFor="note" className="block mb-2 font-bold">
            Note (Optional)
          </label>
          <InputTextarea
            id="note"
            disabled={submitting}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Enter any additional notes..."
            className="w-full"
            style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
          />
        </div>
      </Dialog>
    </>
  );
};

export default AcceptOutwardRequestDialog;
